// External libraries
import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

// Apollo Client
import { useMutation } from "@apollo/client";

// Redux
import { connect } from "react-redux";

// Design System & UI Components
import { LinkOutlined, CaretDownFilled } from "@toddle-edu/ds-icons";
import { Dropdown, DropdownMenu, TextInput } from "@toddle-edu/ds-web";

// Local utilities & constants
import {
  setValue,
  mapStateToProps,
  mapActionCreators,
} from "../../modules/Utils";
import { MEETING_SERVICE_OPTIONS } from "../../../../modules/Utils";

// Local components
import EventRow from "../EventRow/EventRow";
import MeetingLinkDetails from "./MeetingLinkDetails";
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Local hooks
import { useMeetingLink } from "../../hooks";
import { useI18n } from "Hooks";

// Local modules
import { createThirdPartyMeetingLinkMutation } from "MyCalendar/modules/MyCalendarMutation";

// Styles
import classes from "./MeetingLink.scss";

const EMPTY_OBJECT = {};

const useThirdPartyMeetingCreation = ({
  updateEventModalV2,
  eventModalV2,
  setToastMsg,
}) => {
  const setThirdPartyMeetingLinkData = setValue(
    "thirdPartyMeetingLinkData",
    updateEventModalV2
  );

  // Q. Why not use the loading state from useMutation directly?
  // A. Because we have to use a common loading state at both places (Quick view & Full screen),
  // if user starts generating meeting link on quick view and immediately switches to full screen view
  // it should reference to original loading state to show the meeting link.
  const setThirdPartyMeetingLinkLoading = setValue(
    "isCreatingThirdPartyMeetingLink",
    updateEventModalV2
  );

  const handleMeetingLinkCreationError = () => {
    if (setToastMsg) {
      setToastMsg({
        message: "Failed to create meeting link",
        type: "error",
      });
    }
    setThirdPartyMeetingLinkLoading(false);
  };

  const [
    createThirdPartyMeetingLink,
    {
      data: thirdPartyMeetingLinkDataResponse,
      loading: isThirdPartyMeetingLinkDataResponseLoading,
    },
  ] = useMutation(createThirdPartyMeetingLinkMutation, {
    onCompleted: thirdPartyMeetingLinkDataResponse => {
      setThirdPartyMeetingLinkData(
        _.get(
          thirdPartyMeetingLinkDataResponse,
          "platform.createThirdPartyMeetingLink.response",
          EMPTY_OBJECT
        )
      );
      setThirdPartyMeetingLinkLoading(false);
    },
    onError: handleMeetingLinkCreationError,
  });

  const createThirdPartyMeetingLinkWrapper = meetingServiceVariables => {
    setThirdPartyMeetingLinkLoading(true);
    createThirdPartyMeetingLink(meetingServiceVariables);
  };

  const { isCreatingThirdPartyMeetingLink } = eventModalV2 || {};

  return {
    createThirdPartyMeetingLink: createThirdPartyMeetingLinkWrapper,
    isCreatingThirdPartyMeetingLink:
      isCreatingThirdPartyMeetingLink ||
      isThirdPartyMeetingLinkDataResponseLoading,
    thirdPartyMeetingLinkData: _.get(
      thirdPartyMeetingLinkDataResponse,
      "platform.createThirdPartyMeetingLink.response",
      EMPTY_OBJECT
    ),
  };
};
const MeetingLink = props => {
  const {
    eventModalV2,
    updateEventModalV2,
    meetingLinkClass = "",
    testId,
    isFullScreenMode = false,
    setToastMsg,
    showSeparator = true,
  } = props;

  const { t } = useI18n(["calendar", "common"]);

  const {
    createThirdPartyMeetingLink,
    isCreatingThirdPartyMeetingLink,
  } = useThirdPartyMeetingCreation({
    updateEventModalV2,
    eventModalV2,
    setToastMsg,
  });

  const {
    selectedService,
    linkError,
    accountOptions,
    isLinkedAccountsLoading,
    handleServiceSelection,
    onChangeCustomMeetingLink,
    getSelectedServiceLabel,
    thirdPartyMeetingLinkData,
    setThirdPartyMeetingLinkData,
    createThirdPartyMeetingLinkWrapper,
    userId,
    isThirdPartyAuthLoadingOrRefetching,
  } = useMeetingLink({
    eventModalV2,
    updateEventModalV2,
    createThirdPartyMeetingLink,
    isCreatingThirdPartyMeetingLink,
    testId,
    classes,
    setToastMsg,
  });

  const renderDropdownButton = isLoading => {
    const selectedOption = _.find(accountOptions, {
      key: selectedService,
    });

    return (
      <div
        className={classNames("text-body-1", classes.dropdownButton)}
        data-test-id={`${testId}-meeting-service-button`}
      >
        {isLoading ? (
          <SkeletonLoader height="20px" width="200px" variant="rounded" />
        ) : (
          <>
            {selectedOption?.icon}
            <span className={`${classes.dropdownButtonLabel} text-label`}>
              {getSelectedServiceLabel()}
            </span>
            <CaretDownFilled
              size="xx-small"
              variant="primary"
              className={classes.chevronIcon}
            />
          </>
        )}
      </div>
    );
  };

  const renderMeetingLinkInput = () => {
    const inputClassContainer = classNames({
      [classes.inputContainer]: !isFullScreenMode,
      [classes.fullScreenInputContainer]: isFullScreenMode,
    });
    if (_.isEmpty(selectedService)) {
      return null;
    }
    if (selectedService === MEETING_SERVICE_OPTIONS.OTHER) {
      return (
        <div className={inputClassContainer}>
          <TextInput
            onChange={onChangeCustomMeetingLink}
            variant="outlined"
            label={
              <span>
                {t("calendar:add_meeting_link")}
                <sup className={classes.requiredAsterisk}>*</sup>
              </span>
            }
            isRequired={true}
            placeholder={`${t("calendar:enter_custom_meeting_link")} *`}
            defaultValue={eventModalV2?.meetingLink || ""}
            className={classes.textInput}
            testId={`${testId}-meeting-link`}
            error={!!linkError}
            errorMessage={linkError}
          />
        </div>
      );
    }
    const createThirdPartyMeetingLinkHandler = () =>
      createThirdPartyMeetingLinkWrapper(selectedService);
    return (
      <MeetingLinkDetails
        isFullScreenMode={isFullScreenMode}
        isCreatingThirdPartyMeetingLink={
          isCreatingThirdPartyMeetingLink || isThirdPartyAuthLoadingOrRefetching
        }
        thirdPartyMeetingLinkData={thirdPartyMeetingLinkData}
        createThirdPartyMeetingLink={createThirdPartyMeetingLinkHandler}
        testId={`${testId}-MeetingLink`}
        onRemoveMeetingLink={() => {
          setThirdPartyMeetingLinkData({});
        }}
        creatorId={userId}
        showSeparator={showSeparator}
      />
    );
  };

  const containerClass = classNames(classes.meetingLinkContainer, {
    [classes.fullScreenContainer]: isFullScreenMode,
    [classes.customMeetingLinkContainer]: selectedService && !isFullScreenMode,
  });

  const labelClass = classNames(classes.meetingLabel, {
    [classes.fullScreenLabel]: isFullScreenMode,
    [classes.customMeetingLabel]: selectedService && !isFullScreenMode,
  });

  const dropdownContainerClass = classNames({
    [classes.container]: true,
    [meetingLinkClass]: true,
    [classes.fullScreenContentContainer]: isFullScreenMode,
  });
  return (
    <EventRow
      label="calendar:meeting_link"
      icon={<LinkOutlined variant="subtle" />}
      labelClass={labelClass}
      className={containerClass}
      isRequired={true}
      renderComponent={() => (
        <div className={dropdownContainerClass}>
          <Dropdown
            overlay={
              <DropdownMenu
                dsVersion="2.0"
                options={accountOptions}
                onClick={handleServiceSelection}
                testId={`${testId}-meeting-service-dropdown`}
              />
            }
            placement="bottomLeft"
          >
            {renderDropdownButton(
              isThirdPartyAuthLoadingOrRefetching || isLinkedAccountsLoading
            )}
          </Dropdown>
          {renderMeetingLinkInput()}
        </div>
      )}
    />
  );
};

MeetingLink.defaultProps = {
  variant: "plain",
  eventModalV2: {},
  updateEventModalV2: () => {},
};

MeetingLink.propTypes = {
  variant: PropTypes.oneOf(["plain", "outlined"]),
  updateEventModalV2: PropTypes.func,
  eventModalV2: PropTypes.object,
  meetingLinkClass: PropTypes.string,
  testId: PropTypes.string,
  setToastMsg: PropTypes.func,
};

export default connect(mapStateToProps, mapActionCreators)(MeetingLink);
